import { useId } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Input } from "@volca/ui/components/ui/input";
import { Label } from "@volca/ui/components/ui/label";
import { Separator } from "@volca/ui/components/ui/separator";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useAuth } from "@/core/auth";
import { CopyField } from "./copy-field";
import { ForwardingTestResult } from "./forwarding-test-result";
import { FORWARDING_CHECK_STATE, type ForwardingCheck } from "./types";

const BUTTON_LABEL: Record<ForwardingCheck["state"], string> = {
  [FORWARDING_CHECK_STATE.unsent]: "Send test message",
  [FORWARDING_CHECK_STATE.waiting]: "Send another test",
  [FORWARDING_CHECK_STATE.confirmed]: "Test again",
  [FORWARDING_CHECK_STATE.timedOut]: "Send another test",
};

/**
 * The second pane of the forwarding step: where to send the test, the rule that sends it
 * back, and the test itself.
 *
 * The address and the button are one pane because they are one thing. The address exists
 * only so we have a mailbox to post the test into, and a customer who is given it as a
 * separate question has no idea why we are asking.
 */
export const ForwardingTestCard = ({
  check,
  address,
  inbox,
  onInboxChange,
  canSend,
  sending,
  error,
  inboxError,
  onSend,
}: {
  check: ForwardingCheck;
  /** The business's own Volca address, which the rule forwards to. */
  address: string | null;
  inbox: string;
  onInboxChange?: (value: string) => void;
  /** False until there is an address to send to. */
  canSend: boolean;
  sending: boolean;
  error?: string;
  inboxError?: string;
  onSend: () => void;
}) => {
  const { user } = useAuth();
  const emailDomain = user?.email.split("@")[1];
  const inboxPlaceholder = emailDomain
    ? `ap@${emailDomain}`
    : "ap@yourcompany.com";
  const inboxId = useId();
  const errorId = `${inboxId}-error`;
  const confirmed = check.state === FORWARDING_CHECK_STATE.confirmed;

  return (
    <>
      <Card className="w-full gap-0 py-0">
        <CardContent className="p-6">
          <Stack className="w-full" gap={4} align="stretch">
            <Stack className="w-full" align="start" gap={1}>
              <Text variant="title">2. Set up and send a forwarding test</Text>
              <Text tone="muted">
                This test checks that emails from your invoice inbox can reach
                Volca. Create a separate rule just for this test. We send a test
                email to your invoice inbox, and the test rule forwards it to
                Volca.
              </Text>
            </Stack>

            <Stack className="w-full" gap={1} align="stretch">
              <Label htmlFor={inboxId}>Your current AP inbox</Label>
              <Input
                id={inboxId}
                type="email"
                value={inbox}
                readOnly={!onInboxChange}
                aria-invalid={Boolean(inboxError)}
                aria-describedby={inboxError ? errorId : undefined}
                maxLength={255}
                placeholder={inboxPlaceholder}
                onChange={(event) => onInboxChange?.(event.target.value)}
              />
              {inboxError && (
                <Text
                  id={errorId}
                  variant="caption"
                  tone="destructive"
                  role="alert"
                >
                  {inboxError}
                </Text>
              )}
              <Text variant="caption" tone="muted">
                We send the test here. Create the test rule in this mailbox.
              </Text>
            </Stack>

            <Separator />

            {/* Both addresses, spelled out as the rule reads. Pointing at the address on
              the pane above made the customer scroll and hold it in their head while
              typing it into another system. */}
            <Stack className="w-full" gap={2} align="stretch">
              <Text weight="semibold" variant="caption">
                Create a separate rule for the test email
              </Text>
              <CopyField label="From" value={check.probe_sender} />
              {address && <CopyField label="To" value={address} />}
            </Stack>

            <Text tone="muted">
              Set the test rule to forward only messages from this sender to
              your Volca address. The test email has no attachment, so do not
              add an attachment condition. Keep your invoice forwarding rule
              enabled. Save and enable the test rule, then select “Send test
              message”.
            </Text>

            <Stack className="w-full" align="start" direction="row" gap={2}>
              <Button
                variant={confirmed ? "ghost" : "default"}
                onClick={onSend}
                aria-busy={sending}
                disabled={!canSend || sending}
              >
                {BUTTON_LABEL[check.state]}
              </Button>
            </Stack>

            {error && (
              <Text variant="caption" tone="destructive" role="alert">
                {error}
              </Text>
            )}

            {!canSend && (
              <Text variant="caption" tone="muted">
                Add the address your invoices arrive at, then send the test.
              </Text>
            )}
          </Stack>
        </CardContent>
      </Card>
      <ForwardingTestResult check={check} />
    </>
  );
};
